import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'hooks/use-router/use-router';
import { Image } from 'components/elements/image/Image';
import { Wrapper, ImageWrapper, Info } from './ErrorPageContent.styles';
import { usePolyglotT } from 'utils/locale/locale';
import { Heading } from 'components/elements/heading/Heading';
import { Paragraph } from 'components/elements/paragraph/Paragraph';
import { Button } from 'components/elements/button/Button';
import { useSessionContext } from 'utils/context/session-provider';
import { getRedirectURLFromClientID } from 'utils/helpers';
import { Card } from 'components/elements/card/Card';
import { ModalDialog } from 'components/elements/modal-dialog/modal-dialog';
import { useModalHash } from 'hooks/use-modal-hash/use-modal-hash';
import { sanitizeHtml } from 'utils/sanitize';
import { TroubleshootingDetails } from 'components/elements/troubleshooting-details/troubleshooting-details';

export const ErrorPageContent = () => {
  const [searchParams] = useSearchParams();

  const { t } = usePolyglotT();
  const accountHelpModal = useModalHash('account-help');
  const { clientId, redirectBackToClient } = useSessionContext();

  const redirectUri = searchParams.get('redirect_uri') || undefined;
  const [redirectURL, setRedirectURL] = useState<string | undefined>(
    getRedirectURLFromClientID(clientId, redirectUri)
  );

  useEffect(() => {
    setRedirectURL(getRedirectURLFromClientID(clientId, redirectUri));
  }, [clientId, redirectUri]);

  const onClick = useCallback(() => {
    redirectBackToClient();
  }, []);

  return (
    <>
      <Wrapper>
        <Card>
          <ImageWrapper>
            <Image
              src='/images/error-image.svg'
              alt={t('error_page.image_alt')}
            />
          </ImageWrapper>
          <Info>
            <Heading>{t('error_page.heading_1')}</Heading>

            <Paragraph>{t('error_page.description_1')}</Paragraph>

            <Paragraph
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t('error_page.description_2', {
                    helpLink: accountHelpModal.getHashLink(),
                  })
                ),
              }}
            />
            {redirectURL && (
              <Button onClick={onClick} isFull data-testid='back-button'>
                {t('error_page.button')}
              </Button>
            )}
          </Info>
        </Card>
      </Wrapper>

      <ModalDialog
        isOpen={accountHelpModal.isOpen}
        onRequestClose={accountHelpModal.close}
        title={t('troubleshooting_details.heading')}
      >
        <TroubleshootingDetails />
      </ModalDialog>
    </>
  );
};
